import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface BsrTrendPoint {
  date: string
  bsr: number
}

interface BsrTrendChartProps {
  bsrTrend: BsrTrendPoint[]
  competitorName: string
}

function formatDate(value: string): string {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatRank(value: number): string {
  return `#${Math.round(value).toLocaleString('en-US')}`
}

export default function BsrTrendChart({ bsrTrend, competitorName }: BsrTrendChartProps) {
  const points = bsrTrend
    .slice(-30)
    .map((point) => ({
      date: formatDate(point.date),
      bsr: Math.max(1, Number(point.bsr) || 1),
    }))

  if (points.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        No BSR trend data available yet.
      </div>
    )
  }

  return (
    <div className="h-56 rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">BSR Trend (30d)</p>
      <div className="mt-2 h-44">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={20} />
            <YAxis
              reversed
              tick={{ fontSize: 11, fill: '#64748b' }}
              tickFormatter={(value) => formatRank(Number(value))}
              width={72}
            />
            <Tooltip
              formatter={(value: number) => [formatRank(Number(value)), 'BSR']}
              labelFormatter={(label) => `${competitorName} • ${label}`}
            />
            <Line type="monotone" dataKey="bsr" stroke="#059669" strokeWidth={2.25} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-1 text-xs text-slate-500">Lower = better rank</p>
    </div>
  )
}
